import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { isDetailEventFull } from '@/features/events/eventDetail/eventDetailHelpers';
import { type DiscoveryEventDetail as RadiousEventDetail } from '@/lib/api/apiTypes';
import { cn } from '@/lib/utils';
import { BookmarkIcon, PencilLineIcon, XCircleIcon, XIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View } from 'react-native';

export function DirectEntryDownloadBanner({
  detailColumnWidth,
  isSharedLinkEntry = false,
  onDismiss,
  onDownload,
  onSignIn,
}: {
  detailColumnWidth: number;
  isSharedLinkEntry?: boolean;
  onDismiss: () => void;
  onDownload: () => void;
  onSignIn: () => void;
}) {
  return (
    <View className="bg-primary px-4 py-3">
      <View className="w-full self-center gap-2.5" style={{ width: detailColumnWidth }}>
        <View className="flex-row items-start justify-between gap-3">
          <Text className="flex-1 font-body-bold text-sm text-primary-foreground">
            {isSharedLinkEntry
              ? 'Someone shared this event with you. Get Radious to save it, RSVP, and find what else is happening nearby.'
              : "Get Radious to see what's happening near you."}
          </Text>
          <Pressable
            accessibilityRole="button"
            className="size-7 items-center justify-center rounded-full bg-black/10"
            onPress={onDismiss}>
            <Icon as={XIcon} size={16} className="text-primary-foreground" />
          </Pressable>
        </View>

        <View className="flex-row flex-wrap items-center justify-between gap-3">
          <Pressable className="rounded-[20px] bg-card px-[14px] py-2" onPress={onDownload}>
            <Text className="font-body-bold text-xs text-primary">
              {isSharedLinkEntry ? 'Get Radious' : 'Download on the App Store'}
            </Text>
          </Pressable>

          <View className="flex-row items-center gap-3.5">
            <Pressable onPress={onSignIn}>
              <Text className="font-body-bold text-xs text-primary-foreground">Sign in on web</Text>
            </Pressable>

            <Pressable onPress={onDismiss}>
              <Text className="font-body-bold text-xs text-primary-foreground">
                Continue browsing anyway →
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

export function SignInBanner({
  detailColumnWidth,
  onSignIn,
}: {
  detailColumnWidth: number;
  onSignIn: () => void;
}) {
  return (
    <View className="bg-primary px-4 py-3">
      <View
        className="w-full self-center flex-row flex-wrap items-center justify-between gap-3"
        style={{ width: detailColumnWidth }}>
        <Text className="font-body-bold text-[13px] text-primary-foreground">
          Sign in to RSVP, save, comment, and follow.
        </Text>
        <Pressable
          className="rounded-[20px] bg-card px-[14px] py-1.5"
          onPress={onSignIn}>
          <Text className="font-body-bold text-xs text-primary">Sign in</Text>
        </Pressable>
      </View>
    </View>
  );
}

export function LinkCopiedToast({ visible }: { visible: boolean }) {
  if (!visible) {
    return null;
  }

  return (
    <View className="pointer-events-none absolute inset-x-0 bottom-[100px] z-[99] items-center">
      <View className="rounded-[20px] border border-border bg-surface-raised px-4 py-2">
        <Text className="text-[13px] text-foreground">Link copied!</Text>
      </View>
    </View>
  );
}

export function StickyActionBar({
  bottomInset,
  event,
  mode,
  isBusy,
  stickyLabel,
  onBrowseFeed,
  onCancel,
  onEdit,
  onOpenConfirmRsvp,
  onOpenManageRsvp,
  onToggleSaved,
  maxWidth,
}: {
  bottomInset: number;
  event: Pick<RadiousEventDetail, 'is_saved' | 'viewer_rsvp_status' | 'is_rsvp_full' | 'is_at_capacity'>;
  mode: 'manage' | 'readonly' | 'default';
  isBusy: boolean;
  stickyLabel: string;
  onBrowseFeed: () => void;
  onCancel: () => void;
  onEdit: () => void;
  onOpenConfirmRsvp: () => void;
  onOpenManageRsvp: () => void;
  onToggleSaved: () => void;
  maxWidth?: number;
}) {
  const isGoing = event.viewer_rsvp_status === 'going';
  const isFull = isDetailEventFull(event);
  const isSaved = event.is_saved;

  return (
    <View
      className="absolute inset-x-0 bottom-0 border-t border-border-strong bg-background px-4 pt-3"
      style={{ paddingBottom: bottomInset }}>
      <View className="w-full self-center" style={maxWidth ? { maxWidth } : undefined}>
        {mode === 'manage' ? (
          <View className="flex-row gap-3">
            <Button
              className="min-h-11 flex-1 items-center justify-center rounded-[12px] bg-primary"
              disabled={isBusy}
              onPress={onEdit}>
              <Icon as={PencilLineIcon} size={16} className="text-primary-foreground" />
              <Text className="text-primary-foreground">Edit</Text>
            </Button>
            <Button
              variant="outline"
              className="min-h-11 flex-1 items-center justify-center rounded-[12px] border-primary-pressed bg-canvas-inset"
              disabled={isBusy}
              onPress={onCancel}>
              <Icon as={XCircleIcon} size={16} className="text-text-soft" />
              <Text className="text-text-soft">{isBusy ? 'Canceling...' : 'Cancel'}</Text>
            </Button>
          </View>
        ) : mode === 'readonly' ? (
          <View className="flex-row items-center gap-3">
            <SaveButton isBusy={isBusy} isSaved={isSaved} onPress={onToggleSaved} />
            <Button
              className="min-h-11 flex-1 items-center justify-center rounded-[12px] bg-primary"
              onPress={onBrowseFeed}>
              <Text className="text-[15px] font-semibold text-primary-foreground">
                Browse tonight
              </Text>
            </Button>
          </View>
        ) : (
          <View className="flex-row gap-3">
            <SaveButton isBusy={isBusy} isSaved={isSaved} onPress={onToggleSaved} />
            <Button
              className={cn(
                'min-h-11 flex-1 items-center justify-center rounded-[12px]',
                isGoing
                  ? 'border border-primary-edge bg-surface-accent'
                  : isFull
                    ? 'border border-border-strong bg-surface-raised'
                    : 'bg-primary'
              )}
              disabled={isBusy || isFull}
              onPress={isGoing ? onOpenManageRsvp : onOpenConfirmRsvp}>
              <Text
                className={cn(
                  'text-[15px] font-semibold',
                  isGoing
                    ? 'text-primary'
                    : isFull
                      ? 'text-muted-foreground'
                      : 'text-primary-foreground'
                )}>
                {stickyLabel}
              </Text>
            </Button>
          </View>
        )}
      </View>
    </View>
  );
}

function SaveButton({
  isBusy,
  isSaved,
  onPress,
}: {
  isBusy: boolean;
  isSaved: boolean;
  onPress: () => void;
}) {
  return (
    <Button
      variant="outline"
      className={cn(
        'size-11 items-center justify-center rounded-[12px] border-[0.5px]',
        isSaved
          ? 'border-primary-edge bg-primary-soft'
          : 'border-border-strong bg-surface-raised'
      )}
      disabled={isBusy}
      accessibilityLabel={isSaved ? 'Saved' : 'Save'}
      onPress={onPress}>
      <Icon
        as={BookmarkIcon}
        size={18}
        className={cn(isSaved ? 'text-primary' : 'text-muted-foreground')}
        {...({ fill: isSaved ? 'currentColor' : 'transparent' } as any)}
      />
    </Button>
  );
}
